import { calibrate, type Calibration, type CalibrationError } from './calibration';
import { createLaughDetector, type LaughDetector } from './laughDetector';
import { LAUGH_PROFILE, type DetectionProfile } from './profiles';
import type { FaceFrame } from './types';

export type MonitorEvent =
  | { readonly type: 'calibrated'; readonly calibration: Calibration }
  | {
      readonly type: 'calibrationFailed';
      readonly error: CalibrationError;
      /** Cadence mesurée (img/s), pour `tooSlow`. */
      readonly fps?: number;
    }
  /** Rire ou clignement détecté ; `t` = début de l'expression. */
  | { readonly type: 'detected'; readonly t: number; readonly score: number };

export type MonitorPhase = 'idle' | 'calibrating' | 'detecting';

export interface ExpressionMonitorOptions {
  /** Durée de la phase « prends ton air sérieux ». */
  readonly calibrationMs?: number;
  /** Remplace la temporisation du profil (page de debug). */
  readonly timing?: DetectionProfile['timing'];
}

export interface ExpressionMonitor {
  readonly profile: DetectionProfile;
  readonly phase: MonitorPhase;
  /** Dernier score (null : pas de visage). */
  readonly lastScore: number | null;
  readonly calibration: Calibration | null;
  /** Démarre une calibration sur la fenêtre [t0, t0 + calibrationMs[. */
  calibrate(t0: number): void;
  /** Réglage manuel, ou réarmement du détecteur : passe directement en détection. */
  setCalibration(calibration: Calibration): void;
  push(frame: FaceFrame): MonitorEvent[];
}

const fpsOf = (times: readonly number[]): number => {
  const first = times[0];
  const last = times.at(-1);
  return first !== undefined && last !== undefined && last > first
    ? ((times.length - 1) * 1000) / (last - first)
    : 0;
};

/** Enchaîne calibration puis détection d'une expression (rire, clignement) sur le visage local. */
export function createExpressionMonitor(
  profile: DetectionProfile = LAUGH_PROFILE,
  { calibrationMs = 3000, timing = profile.timing }: ExpressionMonitorOptions = {},
): ExpressionMonitor {
  let phase: MonitorPhase = 'idle';
  let lastScore: number | null = null;
  let calibration: Calibration | null = null;
  let detector: LaughDetector | null = null;
  let calibrationStart = 0;
  let samples: (number | null)[] = [];
  let times: number[] = [];

  const startDetecting = (c: Calibration): void => {
    calibration = c;
    detector = createLaughDetector({ threshold: c.threshold, release: c.release, ...timing });
    phase = 'detecting';
  };

  const finishCalibration = (): MonitorEvent => {
    const result = calibrate(samples, profile.calibration);
    const fps = fpsOf(times);
    if (!result.ok) {
      phase = 'idle';
      return { type: 'calibrationFailed', error: result.error };
    }
    if (fps < profile.minFps) {
      phase = 'idle';
      return { type: 'calibrationFailed', error: 'tooSlow', fps };
    }
    startDetecting(result.value);
    return { type: 'calibrated', calibration: result.value };
  };

  return {
    profile,
    get phase() {
      return phase;
    },
    get lastScore() {
      return lastScore;
    },
    get calibration() {
      return calibration;
    },
    calibrate(t0) {
      phase = 'calibrating';
      calibrationStart = t0;
      samples = [];
      times = [];
      calibration = null;
      detector = null;
    },
    setCalibration: startDetecting,
    push(frame) {
      const score = frame.blendshapes ? profile.score(frame.blendshapes) : null;
      lastScore = score;
      if (phase === 'calibrating') {
        if (frame.t < calibrationStart) return [];
        if (frame.t < calibrationStart + calibrationMs) {
          samples.push(score);
          times.push(frame.t);
          return [];
        }
        return [finishCalibration()];
      }
      const hit = detector?.push(frame.t, score);
      return hit ? [{ type: 'detected', ...hit }] : [];
    },
  };
}
